import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Trash2, Edit2, Mail, Phone, Shield, Check, X,
  UserPlus, Settings, ChevronDown, AlertCircle, Search, MoreVertical, CreditCard
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import PaybillConfigSection from '../../components/admin/PaybillConfigSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role definitions with icons and colors
const ROLES = {
  super_admin: { 
    label: 'Super Admin', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    description: 'Full access to all features'
  },
  event_organiser: { 
    label: 'Event Organiser', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Manage event, tickets, orders, and staff'
  },
  checkin_staff: { 
    label: 'Check-in Staff', 
    color: 'bg-green-100 text-green-700 border-green-200',
    description: 'Scan tickets and check in attendees'
  },
  analytics_viewer: { 
    label: 'Analytics Viewer', 
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    description: 'View reports and analytics only'
  }
};

const PERMISSIONS = [
  { key: 'manage_event', label: 'Manage Event', description: 'Edit event details' },
  { key: 'manage_tickets', label: 'Manage Tickets', description: 'Create/edit ticket types' },
  { key: 'manage_orders', label: 'Manage Orders', description: 'View and manage orders' },
  { key: 'check_in', label: 'Check In', description: 'Scan and check in attendees' },
  { key: 'view_analytics', label: 'View Analytics', description: 'Access reports and dashboards' },
  { key: 'manage_staff', label: 'Manage Staff', description: 'Add/remove team members' },
  { key: 'bulk_import', label: 'Bulk Import', description: 'Import attendees from CSV' }
];

// Add/Edit Staff Modal
const StaffModal = ({ isOpen, onClose, staff, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    role: 'checkin_staff',
    permissions: []
  });

  useEffect(() => {
    if (staff) {
      setFormData({
        email: staff.email || '',
        name: staff.name || '',
        phone: staff.phone || '',
        role: staff.role || 'checkin_staff',
        permissions: staff.permissions || []
      });
    } else {
      setFormData({
        email: '',
        name: '',
        phone: '',
        role: 'checkin_staff',
        permissions: []
      });
    }
  }, [staff, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">
            {staff ? 'Edit Staff Member' : 'Add Staff Member'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {staff ? 'Update role and permissions' : 'Invite a team member to help manage this event'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="team@example.com"
              disabled={!!staff}
              data-testid="staff-email-input"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="John Doe"
              data-testid="staff-name-input"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="+254 700 000 000"
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
            <div className="space-y-2">
              {Object.entries(ROLES).map(([key, role]) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.role === key ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={key}
                    checked={formData.role === key}
                    onChange={() => setFormData(prev => ({ ...prev, role: key }))}
                    className="w-4 h-4 text-green-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              disabled={isLoading}
              data-testid="save-staff-btn"
            >
              {isLoading ? 'Saving...' : staff ? 'Update' : 'Add Staff'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Staff Card Component
const StaffCard = ({ member, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const role = ROLES[member.role] || ROLES.checkin_staff;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-lg font-semibold text-gray-600">
              {(member.name || member.email)[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-900">{member.name || 'Unnamed'}</p>
            <p className="text-sm text-gray-500">{member.email}</p>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-32 z-10">
              <button
                onClick={() => { onEdit(member); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" /> Edit
              </button>
              <button
                onClick={() => { onDelete(member); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className={`text-xs px-2 py-1 rounded border ${role.color}`}>
          {role.label}
        </span>
        {!member.accepted_at && (
          <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 border border-yellow-200">
            Pending
          </span>
        )}
      </div>

      {member.phone && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
          <Phone className="w-3 h-3" />
          {member.phone}
        </div>
      )}
    </div>
  );
};

// Main Staff Management Page
const StaffManagementPage = () => {
  const { eventId } = useParams();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [event, setEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'payments'

  // Fetch event details
  const fetchEvent = useCallback(async () => {
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}`);
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  }, [eventId]);

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/staff`);
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff list');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    fetchStaff();
  }, [fetchEvent, fetchStaff]);

  // Add staff member
  const handleAddStaff = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Staff member added successfully');
        setModalOpen(false);
        fetchStaff();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add staff member');
      }
    } catch (error) {
      toast.error('Failed to add staff member');
    } finally {
      setSaving(false);
    }
  };

  // Update staff member
  const handleUpdateStaff = async (data) => {
    if (!editingStaff) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/staff/${editingStaff.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Staff member updated');
        setModalOpen(false);
        setEditingStaff(null);
        fetchStaff();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update staff member');
      }
    } catch (error) {
      toast.error('Failed to update staff member');
    } finally {
      setSaving(false);
    }
  };

  // Delete staff member
  const handleDeleteStaff = async (member) => {
    if (!confirm(`Remove ${member.name || member.email} from the team?`)) return;
    
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/staff/${member.id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Staff member removed');
        fetchStaff();
      } else {
        toast.error('Failed to remove staff member');
      }
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };

  // Filter staff by search
  const filteredStaff = staff.filter(m => 
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by role
  const groupedStaff = filteredStaff.reduce((acc, member) => {
    const role = member.role || 'other';
    if (!acc[role]) acc[role] = [];
    acc[role].push(member);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-management-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Event Settings</h1>
              <p className="text-sm text-gray-500">{event?.name || 'Loading...'}</p>
            </div>
            {activeTab === 'staff' && (
              <Button
                onClick={() => { setEditingStaff(null); setModalOpen(true); }}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="add-staff-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            )}
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-t pt-4">
            <button
              onClick={() => setActiveTab('staff')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'staff' 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-staff"
            >
              <Users className="w-4 h-4" />
              Team Members
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'payments' 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-payments"
            >
              <CreditCard className="w-4 h-4" />
              Payment Settings
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Payment Settings Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <PaybillConfigSection eventId={eventId} eventName={event?.name} />
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-medium text-blue-800 mb-2">About Payment Configuration</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Each event can have its own M-Pesa Paybill number</li>
                <li>• If not configured, the system default will be used</li>
                <li>• Account numbers are auto-generated per order for easy tracking</li>
                <li>• Attendees will see your Paybill details when checking out</li>
              </ul>
            </div>
          </div>
        )}
        
        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <>
        {/* Search & Stats */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search team members..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {staff.length} team members
            </span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-500">Loading team...</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
            <p className="text-gray-500 mb-4">Add staff to help manage this event</p>
            <Button
              onClick={() => setModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add First Team Member
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(ROLES).map(([roleKey, roleData]) => {
              const members = groupedStaff[roleKey];
              if (!members || members.length === 0) return null;
              
              return (
                <div key={roleKey}>
                  <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {roleData.label}s ({members.length})
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {members.map(member => (
                      <StaffCard
                        key={member.id}
                        member={member}
                        onEdit={(m) => { setEditingStaff(m); setModalOpen(true); }}
                        onDelete={handleDeleteStaff}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}
      </main>

      {/* Staff Modal */}
      <AnimatePresence>
        {modalOpen && (
          <StaffModal
            isOpen={modalOpen}
            onClose={() => { setModalOpen(false); setEditingStaff(null); }}
            staff={editingStaff}
            onSave={editingStaff ? handleUpdateStaff : handleAddStaff}
            isLoading={saving}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StaffManagementPage;
